import BatteryFull16 from '@carbon/icons-react/es/battery--full/16';
import Person16 from '@carbon/icons-react/es/person/16';
import WatsonHealthCrossReference16 from '@carbon/icons-react/es/watson-health/cross-reference/16';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { LoadBalancerVpc16 } from 'components/atoms/icons';
import { useTranslation } from 'hooks/use-translation';
import { useGetAllPartnerCompanies } from 'queries/partner';
import { SchedulerResourceType } from '../../resource-types';
import { SchedulerReference } from '../../types';
import { DemandNameCell } from './cells/demand-name-cell';
import { DemandRoleCell } from './cells/demand-role';
import { ColumnConfig } from './types';

const LightText = styled.span`
  color: var(--gray-60);
  margin-left: var(--spacing-02);
`;

interface Props {
  schedulerRef: SchedulerReference;
}
export const useColumnsConfigForDemand = ({
  schedulerRef,
}: Props): ColumnConfig[] => {
  const roleTitle = useTranslation('role');
  const { data: partnerCompanies } = useGetAllPartnerCompanies();
  const partnerCompaniesCount = (partnerCompanies ?? []).length;

  return useMemo(
    () => [
      {
        text: roleTitle,
        width: 344,
        type: 'tree',
        renderer: (arg) => {
          const { record } = arg;
          const { type } = record;

          record.cls = `${type} loading-skeleton`;

          switch (type as SchedulerResourceType) {
            case SchedulerResourceType.DemandRole:
              return (
                <DemandRoleCell
                  roleId={record.get('roleId')}
                  roleName={record.get('roleName')}
                  userCount={record.get('userCount')}
                  userCountByUserType={record.get('userCountByUserType')}
                  onWrapperClick={() => {
                    schedulerRef.current.instance.toggleCollapse(record.id);
                  }}
                  showPartnerUserCount={partnerCompaniesCount !== 0}
                />
              );
            case SchedulerResourceType.DemandCapacity:
              return (
                <DemandNameCell
                  text={<FormattedMessage id="timesheets.column.capacity" />}
                  icon={<BatteryFull16 />}
                />
              );
            case SchedulerResourceType.TotalAllocation:
              return (
                <DemandNameCell
                  onWrapperClick={() => {
                    schedulerRef.current.instance.toggleCollapse(record.id);
                  }}
                  text={
                    <FormattedMessage id="resource_management.demand.cells.allocations" />
                  }
                  icon={<LoadBalancerVpc16 />}
                />
              );
            case SchedulerResourceType.UserTotalAllocation:
              return (
                <DemandNameCell
                  text={
                    <FormattedMessage id="resource_management.demand.cells.user_allocations" />
                  }
                  icon={<Person16 />}
                />
              );
            case SchedulerResourceType.UserTotalSoftAllocation:
              return (
                <DemandNameCell
                  text={
                    <FormattedMessage
                      id="resource_management.demand.cells.user_soft_allocations"
                      values={{
                        light: (chunks) => <LightText>{chunks}</LightText>,
                      }}
                    />
                  }
                  icon={<Person16 />}
                />
              );
            case SchedulerResourceType.UserTotalHardAllocation:
              return (
                <DemandNameCell
                  text={
                    <FormattedMessage
                      id="resource_management.demand.cells.user_hard_allocations"
                      values={{
                        light: (chunks) => <LightText>{chunks}</LightText>,
                      }}
                    />
                  }
                  icon={<Person16 />}
                />
              );
            case SchedulerResourceType.TeamTotalAllocation:
              return (
                <DemandNameCell
                  text={
                    <FormattedMessage id="resource_management.demand.cells.team_allocations" />
                  }
                  icon={<WatsonHealthCrossReference16 />}
                />
              );
            case SchedulerResourceType.TeamTotalSoftAllocation:
              return (
                <DemandNameCell
                  text={
                    <FormattedMessage
                      id="resource_management.demand.cells.team_soft_allocations"
                      values={{
                        light: (chunks) => <LightText>{chunks}</LightText>,
                      }}
                    />
                  }
                  icon={<WatsonHealthCrossReference16 />}
                />
              );
            case SchedulerResourceType.TeamTotalHardAllocation:
              return (
                <DemandNameCell
                  text={
                    <FormattedMessage
                      id="resource_management.demand.cells.team_hard_allocations"
                      values={{
                        light: (chunks) => <LightText>{chunks}</LightText>,
                      }}
                    />
                  }
                  icon={<WatsonHealthCrossReference16 />}
                />
              );
            default:
              return '';
          }
        },
      },
    ],
    [partnerCompaniesCount, roleTitle, schedulerRef],
  );
};
